import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Slider,
  Stack,
} from '@mui/material';
import debounce from 'lodash/debounce';

/**
 * SPEC_College_Search_Filters (v1.1) — Lovable taxonomy filter modals.
 * Mirrors UI-design/src/pages/CollegeSearch.tsx filter groups:
 *   Type & Size · Cost · Outcomes.
 * (Location / Majors / Admission keep their dedicated modals.)
 *
 * Behaviour (spec §Behavior):
 *  - Range sliders apply with a 250ms debounce after the last drag motion.
 *  - Toggle/chip filters apply immediately on click.
 *  - "Show Colleges" flushes any pending slider change, then closes.
 */

// ── Legacy type_of_school code groups (mirrors ModalCollegeInfo) ──────────────
const PUBLIC_CODES = ['G', 'GCI', 'GCO', 'GCW', 'GDI', 'GF', 'GP', 'GS', 'GSL', 'GSR', 'GT'];
const PRIVATE_CODES = ['P', 'PN', 'PP', 'PR'];

const SIZE_OPTIONS = [
  { value: 'SMALL', label: 'Small (<2K)' },
  { value: 'MEDIUM', label: 'Medium (2K–15K)' },
  { value: 'LARGE', label: 'Large (15K–30K)' },
  { value: 'VERY LARGE', label: 'Very Large (30K+)' },
];
// cmps_setting keys mirror ModalCampusLife (rural/small/subrb/urban).
const SETTING_OPTIONS = [
  { value: 'urban', label: 'Urban' },
  { value: 'subrb', label: 'Suburban' },
  { value: 'small', label: 'Town' },
  { value: 'rural', label: 'Rural' },
];
const GENDER_TYPES = ['Coed', 'Women', 'Men'];

const APPLICATION_TYPES = ['Common App', 'UC', 'CSU', 'Coalition', 'Institutional'];
const TESTING_POLICIES = ['Test-Required', 'Test-Optional', 'Test-Blind', 'Sometimes Required'];

const toggleInList = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const norm = (val: number, sentinel: number) => (val === sentinel ? null : val);

// ── Shared draft/debounce hook ───────────────────────────────────────────────
function useFilterDraft<T>(
  open: boolean,
  formik: any,
  fromFormik: (v: any) => T,
  toPatch: (d: T) => Record<string, unknown>,
) {
  const [draft, setDraft] = React.useState<T>(() => fromFormik(formik.values));
  const formikRef = React.useRef(formik);
  formikRef.current = formik;

  const commit = React.useCallback(
    (d: T) => {
      const fmk = formikRef.current;
      fmk.setValues({ ...fmk.values, ...toPatch(d) });
      fmk.handleSubmit();
    },
    [toPatch],
  );
  const debouncedCommit = React.useMemo(() => debounce(commit, 250), [commit]);
  React.useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  React.useEffect(() => {
    if (open) setDraft(fromFormik(formikRef.current.values));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateImmediate = (partial: Partial<T>) =>
    setDraft((prev) => {
      const next = { ...prev, ...partial };
      debouncedCommit.cancel();
      commit(next);
      return next;
    });
  const updateDebounced = (partial: Partial<T>) =>
    setDraft((prev) => {
      const next = { ...prev, ...partial };
      debouncedCommit(next);
      return next;
    });
  const flush = (d: T) => {
    debouncedCommit.cancel();
    commit(d);
  };

  return { draft, updateImmediate, updateDebounced, flush };
}

// ── Presentational helpers ───────────────────────────────────────────────────
interface SectionLabelProps {
  children: React.ReactNode;
  first?: boolean;
}
const SectionLabel: React.FC<SectionLabelProps> = ({ children, first }) => (
  <Box className={`fw-bold fs-5 ${first ? 'mt-2' : 'mt-4'}`}>{children}</Box>
);

interface ChipOption {
  value: string;
  label?: string;
}
interface ChipRowProps {
  options: ChipOption[] | string[];
  selected: string[];
  onToggle: (value: string) => void;
}
const ChipRow: React.FC<ChipRowProps> = ({ options, selected, onToggle }) => {
  const opts: ChipOption[] = (options as Array<ChipOption | string>).map((option) => {
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    return option;
  });

  return (
    <Stack direction="row" flexWrap="wrap" gap={1} className="mt-2">
      {opts.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <Chip
            key={opt.value}
            label={opt.label ?? opt.value}
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => onToggle(opt.value)}
          />
        );
      })}
    </Stack>
  );
};

interface RangeRowProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number[];
  onChange: (v: number[]) => void;
  endLabels: [string, string];
}
const RangeRow: React.FC<RangeRowProps> = ({ label, min, max, step, value, onChange, endLabels }) => (
  <>
    <SectionLabel>{label}</SectionLabel>
    <Box className="m-3">
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(_e, nv) => onChange(nv as number[])}
        valueLabelDisplay="auto"
        marks={[
          { value: min, label: endLabels[0] },
          { value: max, label: endLabels[1] },
        ]}
      />
    </Box>
  </>
);

interface ShowCollegesActionsProps {
  onClick: () => void;
}
const ShowCollegesActions: React.FC<ShowCollegesActionsProps> = ({ onClick }) => (
  <DialogActions className="p-4">
    <Button variant="contained" onClick={onClick}>
      Show Colleges
    </Button>
  </DialogActions>
);

// ── Type & Size ──────────────────────────────────────────────────────────────
interface TypeSizeDraft {
  typePublic: boolean;
  typePrivate: boolean;
  sizes: string[];
  settings: string[];
  genders: string[];
}
const typeSizeFromFormik = (v: any): TypeSizeDraft => ({
  typePublic: (v.type_of_school ?? []).includes('G'),
  typePrivate: (v.type_of_school ?? []).includes('P'),
  sizes: v.size_of_school ?? [],
  settings: v.cmps_setting ?? [],
  genders: v.gender_types ?? [],
});
const typeSizeToPatch = (d: TypeSizeDraft) => ({
  type_of_school: [
    ...(d.typePublic ? PUBLIC_CODES : []),
    ...(d.typePrivate ? PRIVATE_CODES : []),
  ],
  size_of_school: d.sizes,
  cmps_setting: d.settings,
  gender_types: d.genders,
});

export const ModalTypeSize = ({ open, setOpen, formik }: any) => {
  const { draft, updateImmediate, flush } = useFilterDraft<TypeSizeDraft>(
    open,
    formik,
    typeSizeFromFormik,
    typeSizeToPatch,
  );
  return (
    <Dialog open={open} onClose={() => setOpen(false)} keepMounted fullWidth maxWidth="sm">
      <DialogTitle className="fw-bold fs-3">Type &amp; Size</DialogTitle>
      <DialogContent>
        <SectionLabel first>Institution Type</SectionLabel>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.typePublic}
                onChange={(e) => updateImmediate({ typePublic: e.target.checked })}
              />
            }
            label="Public"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.typePrivate}
                onChange={(e) => updateImmediate({ typePrivate: e.target.checked })}
              />
            }
            label="Private"
          />
        </FormGroup>

        <SectionLabel>School Size</SectionLabel>
        <ChipRow
          options={SIZE_OPTIONS}
          selected={draft.sizes}
          onToggle={(val) => updateImmediate({ sizes: toggleInList(draft.sizes, val) })}
        />

        <SectionLabel>Campus Setting</SectionLabel>
        <ChipRow
          options={SETTING_OPTIONS}
          selected={draft.settings}
          onToggle={(val) => updateImmediate({ settings: toggleInList(draft.settings, val) })}
        />

        <SectionLabel>Gender Composition</SectionLabel>
        <ChipRow
          options={GENDER_TYPES}
          selected={draft.genders}
          onToggle={(val) => updateImmediate({ genders: toggleInList(draft.genders, val) })}
        />
      </DialogContent>
      <ShowCollegesActions
        onClick={() => {
          flush(draft);
          setOpen(false);
        }}
      />
    </Dialog>
  );
};

// ── Cost ─────────────────────────────────────────────────────────────────────
interface CostDraft {
  inStateTuition: number[];
  outStateTuition: number[];
  roomBoard: number[];
  netPrice: number[];
  offersMeritAid: boolean;
  meetsFullNeed: boolean;
  loanFree: boolean;
  freeTuition: boolean;
}
const costFromFormik = (v: any): CostDraft => ({
  inStateTuition: [v.from_in_state_tuition ?? 0, v.to_in_state_tuition ?? 80000],
  outStateTuition: [v.from_out_of_state_tuition ?? 0, v.to_out_of_state_tuition ?? 80000],
  roomBoard: [v.from_room_board ?? 0, v.to_room_board ?? 25000],
  netPrice: [v.from_net_price ?? 0, v.to_net_price ?? 80000],
  offersMeritAid: !!v.offers_merit_aid,
  meetsFullNeed: !!v.meets_full_need,
  loanFree: !!v.has_loan_free_policy,
  freeTuition: !!v.has_free_tuition_policy,
});
const costToPatch = (d: CostDraft) => ({
  from_in_state_tuition: norm(d.inStateTuition[0], 0),
  to_in_state_tuition: norm(d.inStateTuition[1], 80000),
  from_out_of_state_tuition: norm(d.outStateTuition[0], 0),
  to_out_of_state_tuition: norm(d.outStateTuition[1], 80000),
  from_room_board: norm(d.roomBoard[0], 0),
  to_room_board: norm(d.roomBoard[1], 25000),
  from_net_price: norm(d.netPrice[0], 0),
  to_net_price: norm(d.netPrice[1], 80000),
  offers_merit_aid: d.offersMeritAid,
  meets_full_need: d.meetsFullNeed,
  has_loan_free_policy: d.loanFree,
  has_free_tuition_policy: d.freeTuition,
});

export const ModalCost = ({ open, setOpen, formik }: any) => {
  const { draft, updateImmediate, updateDebounced, flush } = useFilterDraft<CostDraft>(
    open,
    formik,
    costFromFormik,
    costToPatch,
  );
  return (
    <Dialog open={open} onClose={() => setOpen(false)} keepMounted fullWidth maxWidth="sm">
      <DialogTitle className="fw-bold fs-3">Cost</DialogTitle>
      <DialogContent>
        <RangeRow
          label="Tuition (In-State)"
          min={0}
          max={80000}
          step={1000}
          value={draft.inStateTuition}
          onChange={(nv) => updateDebounced({ inStateTuition: nv })}
          endLabels={['$0', '$80K']}
        />
        <RangeRow
          label="Tuition (Out-of-State)"
          min={0}
          max={80000}
          step={1000}
          value={draft.outStateTuition}
          onChange={(nv) => updateDebounced({ outStateTuition: nv })}
          endLabels={['$0', '$80K']}
        />
        <RangeRow
          label="Room &amp; Board"
          min={0}
          max={25000}
          step={500}
          value={draft.roomBoard}
          onChange={(nv) => updateDebounced({ roomBoard: nv })}
          endLabels={['$0', '$25K']}
        />
        <RangeRow
          label="Avg Net Price (after aid)"
          min={0}
          max={80000}
          step={1000}
          value={draft.netPrice}
          onChange={(nv) => updateDebounced({ netPrice: nv })}
          endLabels={['$0', '$80K']}
        />
        <FormGroup className="mt-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.offersMeritAid}
                onChange={(e) => updateImmediate({ offersMeritAid: e.target.checked })}
              />
            }
            label="Offers Merit Aid"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.meetsFullNeed}
                onChange={(e) => updateImmediate({ meetsFullNeed: e.target.checked })}
              />
            }
            label="Meets Full Financial Need"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.loanFree}
                onChange={(e) => updateImmediate({ loanFree: e.target.checked })}
              />
            }
            label="Loan-Free Policy"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.freeTuition}
                onChange={(e) => updateImmediate({ freeTuition: e.target.checked })}
              />
            }
            label="Free Tuition Policy (income-based)"
          />
        </FormGroup>
      </DialogContent>
      <ShowCollegesActions
        onClick={() => {
          flush(draft);
          setOpen(false);
        }}
      />
    </Dialog>
  );
};

// ── Outcomes ─────────────────────────────────────────────────────────────────
interface OutcomesDraft {
  earnings: number[];
  employment: number[];
  grad4: number[];
  grad6: number[];
  retention: number[];
  maxDefault: number;
}
const outcomesFromFormik = (v: any): OutcomesDraft => ({
  earnings: [v.from_median_earnings ?? 0, v.to_median_earnings ?? 150000],
  employment: [v.from_employment_rate ?? 0, v.to_employment_rate ?? 100],
  grad4: [v.from_graduation_rate_4yr ?? 0, v.to_graduation_rate_4yr ?? 100],
  grad6: [v.from_graduation_rate ?? 0, v.to_graduation_rate ?? 100],
  retention: [v.from_retention_rate ?? 0, v.to_retention_rate ?? 100],
  maxDefault: v.to_loan_default_rate ?? 100,
});
const outcomesToPatch = (d: OutcomesDraft) => ({
  from_median_earnings: norm(d.earnings[0], 0),
  to_median_earnings: norm(d.earnings[1], 150000),
  from_employment_rate: norm(d.employment[0], 0),
  to_employment_rate: norm(d.employment[1], 100),
  from_graduation_rate_4yr: norm(d.grad4[0], 0),
  to_graduation_rate_4yr: norm(d.grad4[1], 100),
  from_graduation_rate: norm(d.grad6[0], 0),
  to_graduation_rate: norm(d.grad6[1], 100),
  from_retention_rate: norm(d.retention[0], 0),
  to_retention_rate: norm(d.retention[1], 100),
  to_loan_default_rate: norm(d.maxDefault, 100),
});

export const ModalOutcomes = ({ open, setOpen, formik }: any) => {
  const { draft, updateDebounced, flush } = useFilterDraft<OutcomesDraft>(
    open,
    formik,
    outcomesFromFormik,
    outcomesToPatch,
  );
  return (
    <Dialog open={open} onClose={() => setOpen(false)} keepMounted fullWidth maxWidth="sm">
      <DialogTitle className="fw-bold fs-3">Outcomes</DialogTitle>
      <DialogContent>
        <RangeRow
          label="Median Earnings 10yr After Grad"
          min={0}
          max={150000}
          step={5000}
          value={draft.earnings}
          onChange={(nv) => updateDebounced({ earnings: nv })}
          endLabels={['$0', '$150K']}
        />
        <RangeRow
          label="Employment Rate (%)"
          min={0}
          max={100}
          step={1}
          value={draft.employment}
          onChange={(nv) => updateDebounced({ employment: nv })}
          endLabels={['0%', '100%']}
        />
        <RangeRow
          label="4-Year Graduation Rate (%)"
          min={0}
          max={100}
          step={1}
          value={draft.grad4}
          onChange={(nv) => updateDebounced({ grad4: nv })}
          endLabels={['0%', '100%']}
        />
        <RangeRow
          label="6-Year Graduation Rate (%)"
          min={0}
          max={100}
          step={1}
          value={draft.grad6}
          onChange={(nv) => updateDebounced({ grad6: nv })}
          endLabels={['0%', '100%']}
        />
        <RangeRow
          label="1st Year Retention Rate (%)"
          min={0}
          max={100}
          step={1}
          value={draft.retention}
          onChange={(nv) => updateDebounced({ retention: nv })}
          endLabels={['0%', '100%']}
        />
        <SectionLabel>Loan Default Rate (max %)</SectionLabel>
        <Box className="m-3">
          <Slider
            min={0}
            max={100}
            step={1}
            value={draft.maxDefault}
            onChange={(_e, nv) => updateDebounced({ maxDefault: nv as number })}
            valueLabelDisplay="auto"
            marks={[
              { value: 0, label: '0%' },
              { value: 100, label: '100%' },
            ]}
          />
        </Box>
      </DialogContent>
      <ShowCollegesActions
        onClick={() => {
          flush(draft);
          setOpen(false);
        }}
      />
    </Dialog>
  );
};

export { APPLICATION_TYPES, TESTING_POLICIES };
